/**
 * カート・チェックアウトの dataLayer プッシュ（GA4 / GTM 用）
 *
 * Shopify のカスタムピクセル側でも同名イベントが送られている可能性があるため、
 * テーマ側のイベント名には fs_ を付ける（GTM 側で GA4 のイベント名に読み替える）。
 *
 * - fs_add_to_cart      : カートの中身が増えたとき（差分の items だけ）
 * - fs_remove_from_cart : カートの中身が減ったとき（差分の items だけ）
 * - fs_view_cart        : カートページ表示時・カートドロワーを開いたとき
 * - fs_begin_checkout   : チェックアウトボタン押下時
 *
 * 仕組み: component-datalayer-page.liquid が出力する window.fsCartSnapshot を起点に、
 * テーマのカート更新イベント（product:added / cart:item-added / cart:refresh）と
 * /cart/add|change|update|clear への fetch / XHR の完了後に /cart.js を取り直して差分を送る。
 * 通常のフォーム送信（ページ遷移を伴うカート追加）は、sessionStorage に残した前回の状態との差分で拾う。
 */
(() => {
  window.dataLayer = window.dataLayer || [];

  const STORAGE_KEY = 'fs_cart_snapshot';
  const CART_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?$/;

  const listAttr = (productId) => (typeof window.fsListAttr === 'function' ? window.fsListAttr(productId) : {});

  // /cart.js のアイテムと Liquid 出力のアイテムを同じ形に揃える
  const normalize = (item) => ({
    product_id: item.product_id,
    variant_id: item.variant_id,
    product_title: item.product_title,
    variant_title: item.variant_title,
    vendor: item.vendor,
    product_type: item.product_type,
    sku: item.sku,
    price: typeof item.final_price === 'number' ? item.final_price / 100 : Number(item.price),
    quantity: item.quantity,
  });

  const toGaItem = (item, quantity) => ({
    item_id: String(item.product_id),
    item_name: item.product_title,
    item_brand: item.vendor,
    item_category: item.product_type,
    item_variant: String(item.variant_id),
    price: item.price,
    quantity,
    ...listAttr(item.product_id),
  });

  const sum = (items) => Math.round(items.reduce((t, i) => t + i.price * i.quantity, 0) * 100) / 100;

  const push = (event, items, extra = {}) => {
    if (!items.length) return;
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event,
      ecommerce: { currency: snapshot.currency, value: sum(items), items: items.map((i) => toGaItem(i, i.quantity)) },
      ...extra,
    });
  };

  // ---- スナップショット -------------------------------------------------
  let snapshot = window.fsCartSnapshot || { total: 0, currency: 'JPY', items: [] };
  snapshot.items = snapshot.items.map(normalize);

  const byVariant = (items) => {
    const map = {};
    items.forEach((i) => {
      const key = String(i.variant_id);
      map[key] = map[key] ? { ...map[key], quantity: map[key].quantity + i.quantity } : { ...i };
    });
    return map;
  };

  const diff = (prev, next) => {
    const a = byVariant(prev);
    const b = byVariant(next);
    const added = [];
    const removed = [];
    Object.keys(b).forEach((k) => {
      const d = b[k].quantity - (a[k] ? a[k].quantity : 0);
      if (d > 0) added.push({ ...b[k], quantity: d });
    });
    Object.keys(a).forEach((k) => {
      const d = a[k].quantity - (b[k] ? b[k].quantity : 0);
      if (d > 0) removed.push({ ...a[k], quantity: d });
    });
    return { added, removed };
  };

  const persist = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch (_) {}
  };

  const apply = (next, { emitRemoved = true } = {}) => {
    const { added, removed } = diff(snapshot.items, next.items);
    snapshot = next;
    persist();
    push('fs_add_to_cart', added);
    if (emitRemoved) push('fs_remove_from_cart', removed);
  };

  // ページ遷移を伴うカート追加（通常のフォーム送信・別タブでの追加）を、前回の状態との差分で拾う。
  // 購入完了後などカートが空になっているケースを削除扱いにしないため、ページ読み込み時は追加分だけ送る。
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (stored && Array.isArray(stored.items)) {
      const current = snapshot;
      snapshot = stored;
      apply(current, { emitRemoved: false });
    } else {
      persist();
    }
  } catch (_) {
    persist();
  }

  // ---- /cart.js を取り直して差分を送る（直列化して二重送信を防ぐ） -------
  let queue = Promise.resolve();
  const refresh = () => {
    queue = queue
      .then(() => fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }))
      .then((r) => r.json())
      .then((cart) => {
        apply({
          total: cart.total_price / 100,
          currency: cart.currency || snapshot.currency,
          items: (cart.items || []).map(normalize),
        });
      })
      .catch(() => {});
    return queue;
  };

  ['product:added', 'cart:item-added', 'cart:refresh'].forEach((name) => {
    document.addEventListener(name, () => refresh(), true);
  });

  // fetch / XHR でカート API が呼ばれたら、完了後に取り直す（アプリや個別スニペットからの追加も拾う）
  const isCartUrl = (url) => {
    try {
      return CART_URL_RE.test(new URL(String(url), location.href).pathname);
    } catch (_) {
      return false;
    }
  };
  if (window.fetch) {
    const origFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input && input.url;
      const p = origFetch.apply(this, arguments);
      if (isCartUrl(url)) p.then(() => refresh(), () => {});
      return p;
    };
  }
  if (window.XMLHttpRequest) {
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (isCartUrl(url)) this.addEventListener('loadend', () => refresh());
      return origOpen.apply(this, arguments);
    };
  }

  // ---- fs_view_cart --------------------------------------------------------
  const viewCart = (location_type) => {
    push('fs_view_cart', snapshot.items, { cart_location: location_type });
  };
  if (window.fsPageData && window.fsPageData.template === 'cart') {
    viewCart('page');
  }
  const drawer = document.getElementById('sidebar-cart');
  if (drawer) {
    new MutationObserver(() => {
      if (drawer.getAttribute('aria-hidden') === 'false') viewCart('drawer');
    }).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  // ---- fs_begin_checkout ---------------------------------------------------
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const button = target && target.closest('button[name="checkout"], input[name="checkout"]');
      if (!button || button.disabled) return;
      push('fs_begin_checkout', snapshot.items, {
        cart_location: button.closest('#sidebar-cart') ? 'drawer' : 'page',
      });
    },
    true
  );
})();
